//SelectCompanyGroupDialog
//***********************************************************************************************************************
import * as React from "react";
import { AppUtils } from "../../../framework/appUtils";
import * as messageService from "../../../models/services/messageService";
import { useEffect, useState } from "react";
import { IGroupPermission } from "source/models/common/groupPermission";
import { EnumHelper, GroupPermissionLevel, GroupType } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { Translations } from "ui/Scripts/source/models/translations";
import { Box, FormControlLabel, Radio, Stack, Typography } from "@mui/material";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { AddToolButton } from "source/components/framework/Button/AddToolButton";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { SelectCompanyGroupForPermissionsDialog } from "../SelectCompanyGroupForPermissionsDialog";
import { ICompanyGroupForPermissionListItem } from "source/models/companyGroup/companyGroupForPermissionListItem";
import { SaveDataObj } from "source/framework/saveData";
import { ISessionState } from "../../../models/session/sessionState";
import { CodeTitle, ICodeTitle } from "source/models/common/codeTitle";
import { INumberTitle, NumberTitle } from "ui/Scripts/source/models/common/numberTitle";
import { List } from "../../framework/List/List";

export interface ICompanyGroupPermissionSettingsProps {
    sessionState: ISessionState;
    companyId: string;
    canIncreasePermissions?: boolean;
    levels: INumberTitle[];
    maxPermissionLevel: GroupPermissionLevel;
}

interface IPermissionSummaryLine {
    id: string;
    title: string;
    permission: string;
}

export interface ICompanyGroupPermissionSettingsState {
    basePermission: number;
    hasNormalCompanyGroups: boolean;
    hasFixedCompanyGroups: boolean;
    permissions: IGroupPermission[];
    companyGroups: ICodeTitle[];
    permissionSummaryLines: IPermissionSummaryLine[];
}

interface ISelectedGroupPermission {
    title: string;
    level: number;
    selected: boolean;
    inherited: boolean;
    disabled: boolean;
}

export interface ISelectedPermissionsState {
    selectedGroupCode: string;
    selectedGroupPermissions: ISelectedGroupPermission[];
}

export const CompanyGroupPermissionSettings = (props: ICompanyGroupPermissionSettingsProps) => {
    const [showSelectUserGroupDialog, setShowSelectUserGroupDialog] = useState(false);
    const [selectedGroupState, setSelectedGroupState] = useState<ISelectedPermissionsState>({
        selectedGroupCode: "",
        selectedGroupPermissions: []
    });
    const [state, setState] = useState<ICompanyGroupPermissionSettingsState>({
        basePermission: 0,
        hasNormalCompanyGroups: false,
        hasFixedCompanyGroups: false,
        permissions: [],
        companyGroups: [],
        permissionSummaryLines: [],
    });

    const changeState = (obj: any) => {
        setState(prevState => ({
            ...prevState,
            ...obj
        }));
    };

    const changeSelectedGroupState = (obj: any) => {
        setSelectedGroupState(prevState => ({
            ...prevState,
            ...obj
        }));
    };

    const getPermissionAuthorizationLevel = (permissions: IGroupPermission[], code: string, defaultValue: GroupPermissionLevel): GroupPermissionLevel => {
        let allPermission = defaultValue;
        for (const permission of permissions) {
            if (permission.code === code) {
                return permission.level;
            } else if (!permission.code) { //Special inherited user group base permission for shared folders OR implicit group for 'all'
                allPermission = permission.level;
            }
        }
        return allPermission;
    };

    const getSelectedGroupPermissions = (permissions: IGroupPermission[], code: string, canIncreasePermissions: boolean, maxPermissionLevel: GroupPermissionLevel): ISelectedGroupPermission[] => {
        if (Base.isNullOrUndefined(code)) return [];
        const permission = getPermissionAuthorizationLevel(permissions, code, state.basePermission);
        return props.levels.map(level => {
            return {
                level: level.number,
                title: level.title,
                selected: EnumHelper.isEqual(level.number, permission),
                inherited: EnumHelper.isEqual(level.number, state.basePermission),
                disabled: (!canIncreasePermissions && EnumHelper.isGreaterThan(level.number, state.basePermission)) || (canIncreasePermissions && EnumHelper.isGreaterThan(level.number, maxPermissionLevel))
            };
        });
    };

    const getPermissionSummaryLines = (permissions: IGroupPermission[], companyGroups: ICodeTitle[]): IPermissionSummaryLine[] => {
        const result: IPermissionSummaryLine[] = [];
        for (const group of companyGroups) {
            const permission = permissions.find(i => i.code === group.code);
            if (!permission) continue;
            result.push({ id: group.code, title: group.title, permission: NumberTitle.getTitleByNumber(permission.level, props.levels) });
        }
        return result;
    };

    const loadData = async () => {
        if (!props.companyId) return;
        const result = await AppUtils.callService(() => messageService.getCompanyGroupPermissions(props.companyId), false);
        if (!result) return;
        changeState({
            basePermission: Math.min(result.basePermission, GroupPermissionLevel.Write),
            hasNormalCompanyGroups: result.hasNormalCompanyGroups,
            hasFixedCompanyGroups: result.hasFixedCompanyGroups,
            inheritedPermissions: result.inheritedPermissions,
            permissions: result.permissions,
            companyGroups: result.companyGroups,
            permissionSummaryLines: getPermissionSummaryLines(result.permissions, result.companyGroups)
        });
        const selectedGroupCode = result.companyGroups.length > 0 ? result.companyGroups[0].code : "";
        changeSelectedGroupState({
            selectedGroupCode,
            selectedGroupPermissions: getSelectedGroupPermissions(result.permissions, selectedGroupCode, props.canIncreasePermissions, props.maxPermissionLevel),
        });
    };

    useEffect(() => {
        loadData();
    }, [props.companyId]);

    const changeCompanyGroupCode = (selectedGroupCode: string) => {
        changeSelectedGroupState({
            selectedGroupCode,
            selectedGroupPermissions: getSelectedGroupPermissions(state.permissions, selectedGroupCode, props.canIncreasePermissions, props.maxPermissionLevel),
        });
    };

    const saveToDb = async (permissions: IGroupPermission[]): Promise<boolean> => {
        const saveData = new SaveDataObj();
        saveData.append("companyId", props.companyId);
        saveData.append("permissions", permissions);
        //Call server
        return !!(await AppUtils.callService(() => messageService.saveCompanyGroupPermissions(saveData.obj), false));
    };

    // #region SelectCompanyGroupForPermissionsDialog
    const handleCompanyGroup = () => {
        setShowSelectUserGroupDialog(true);
    };

    const handleSelectCompanyGroupForPermissionsDialogOk = async (addCompanyGroups: ICompanyGroupForPermissionListItem[]) => {
        if (addCompanyGroups === null || !addCompanyGroups.length) return;
        const allPermission = state.permissions.find(i => !i.code);
        const newPermissions = addCompanyGroups.map(i => {
            return {
                id: Base.emptyGuid,
                code: i.code,
                level: allPermission ? allPermission.level : state.basePermission,
                type: GroupType.CompanyGroup
            };
        });
        const permissions = state.permissions.concat(newPermissions);
        if (!saveToDb(permissions)) return;
        const companyGroups = CodeTitle.sortCodeTitles(state.companyGroups.concat(addCompanyGroups.map(i => CodeTitle.createCodeTitle(i.code, i.name))), "title", true);
        setShowSelectUserGroupDialog(false);
        changeState({
            permissions,
            companyGroups,
            permissionSummaryLines: getPermissionSummaryLines(permissions, companyGroups)
        });
        changeSelectedGroupState({
            selectedGroupCode: addCompanyGroups[0].code,
            selectedGroupPermissions: getSelectedGroupPermissions(permissions, addCompanyGroups[0].code, props.canIncreasePermissions, props.maxPermissionLevel),
        });
    };

    const handleSelectCompanyGroupForPermissionsDialogCancel = () => {
        setShowSelectUserGroupDialog(false);
    };
    // #endregion SelectCompanyGroupForPermissionsDialog
    
    const handleRemoveUserGroup = () => {
        if (!selectedGroupState.selectedGroupCode) return;
        const permissions = state.permissions.filter(i => i.code !== selectedGroupState.selectedGroupCode);
        if (!saveToDb(permissions)) return;
        const companyGroups = state.companyGroups.filter(i => i.code !== selectedGroupState.selectedGroupCode);
        const selectedGroupCode = companyGroups.length > 0 ? companyGroups[0].code : "";
        changeState({
            permissions,
            companyGroups,
            permissionSummaryLines: getPermissionSummaryLines(permissions, companyGroups)
        });
        changeSelectedGroupState({
            selectedGroupCode,
            selectedGroupPermissions: getSelectedGroupPermissions(permissions, selectedGroupCode, props.canIncreasePermissions, props.maxPermissionLevel),
        });
    };

    const handleChangeUserGroupPermission = (checked: boolean, level: number) => {
        if (!checked) return;
        const permissions = state.permissions.slice(0);
        const permission = permissions.find(i => i.code === selectedGroupState.selectedGroupCode);
        if (!permission) return;
        const oldLevel = permission.level;
        permission.level = level;
        if (!permission.code) {
            for (const tempPermission of permissions) {
                if (!tempPermission.code || tempPermission.level !== oldLevel) continue;
                tempPermission.level = level;
            }
        }
        if (!saveToDb(permissions)) return;
        changeState({
            permissions,
            permissionSummaryLines: getPermissionSummaryLines(permissions, state.companyGroups)
        });
        changeSelectedGroupState({
            selectedGroupPermissions: getSelectedGroupPermissions(permissions, selectedGroupState.selectedGroupCode, props.canIncreasePermissions, props.maxPermissionLevel),
        });
    };

    return (
        <>
            <DialogSettingsCard
                fitToContainerHeight
                title={Translations.PermissionsOtherCompanies}
                cardContent={<Box sx={{ margin: 0 }}>
                    <ActionContainer
                        leftItems={<IdTitleDropdown
                            selectedId={selectedGroupState.selectedGroupCode}
                            items={state.companyGroups.map(i => { return { id: i.code, title: i.title }; })}
                            onChange={changeCompanyGroupCode}
                        />}
                        rightItems={<Box sx={{ marginLeft: ".5rem" }}>
                            <AddToolButton
                                tooltip={Translations.AddUserGroup}
                                onClick={handleCompanyGroup} />
                            <RemoveToolButton
                                disabled={!selectedGroupState.selectedGroupCode}
                                tooltip={Translations.RemoveUserGroup}
                                onClick={handleRemoveUserGroup} />
                        </Box>}
                    />
                    <Box sx={{ marginBottom: "1rem" }}>
                        <Typography variant="h6" fontWeight={600} fontSize="1rem">
                            {Translations.Permission}
                        </Typography>
                        <Stack>
                            {selectedGroupState.selectedGroupPermissions.map((selectedGroupPermission) => <FormControlLabel
                                key={selectedGroupPermission.level}
                                label={selectedGroupPermission.title}
                                control={<Radio
                                    checked={selectedGroupPermission.selected}
                                    disabled={selectedGroupPermission.disabled}
                                    onChange={(_e, checked: boolean) => handleChangeUserGroupPermission(checked, selectedGroupPermission.level)} />}
                                sx={{ marginBottom: 0 }} />
                            )}
                        </Stack>
                    </Box>
                    <Typography variant="h6" fontWeight={600} fontSize="1rem">
                        {Translations.ActiveUserRights}
                    </Typography>
                </Box>}
                content={
                    <List
                        intialized={true}
                        isLoading={false}
                        hasMore={false}
                        height="100%"
                        header={{
                            columns: [
                                { size: 6, content: Translations.CompanyGroup + "/" + Translations.Company },
                                { size: 6, content: Translations.Permission },
                            ]
                        }}
                        lines={state.permissionSummaryLines.map((permissionSummaryLine) => {
                            return {
                                columns: [
                                    { size: 6, content: permissionSummaryLine.title },
                                    { size: 6, content: permissionSummaryLine.permission }
                                ],
                                id: permissionSummaryLine.id
                            };
                        })}
                        sortColumn={""}
                        sortOrderIsAsc={true}
                        selectedId={selectedGroupState.selectedGroupCode}
                        onSetSelectedIds={(selectedId: string) => changeCompanyGroupCode(selectedId)}
                        onSearchLines={null}
                    />
                }
            />
            {showSelectUserGroupDialog &&
                <SelectCompanyGroupForPermissionsDialog
                    companyId={props.companyId}
                    title={state.hasNormalCompanyGroups ? Translations.SelectCompanyGroupOrCompany : Translations.SelectCompany}
                    ignoreCompanyGroupCodes={state.companyGroups.map(i => i.code)}
                    multiSelect={true}
                    messageTargetMode={false}
                    hasNormalCompanyGroups={state.hasNormalCompanyGroups}
                    hasFixedCompanyGroups={state.hasFixedCompanyGroups}
                    onOk={handleSelectCompanyGroupForPermissionsDialogOk}
                    onCancel={handleSelectCompanyGroupForPermissionsDialogCancel}
                />
            }
        </>
    );
};
